import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useState, useEffect } from 'react';

const HeroSection = () => {
  const [counts, setCounts] = useState({ countries: 0, support: 0, accuracy: 0 });
  const [trackingInput, setTrackingInput] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    const duration = 2000;
    const steps = 60;
    const interval = duration / steps;
    const targets = { countries: 50, support: 24, accuracy: 99.9 };
    let step = 0;

    const timer = setInterval(() => {
      step++;
      const progress = step / steps;
      setCounts({
        countries: Math.floor(targets.countries * progress),
        support: Math.floor(targets.support * progress),
        accuracy: parseFloat((targets.accuracy * progress).toFixed(1)),
      });
      if (step >= steps) {
        clearInterval(timer);
        setCounts(targets);
      }
    }, interval);

    return () => clearInterval(timer);
  }, []);

  const handleTrack = (e: React.FormEvent) => {
    e.preventDefault();
    if (trackingInput.trim()) {
      navigate(`/tracking?track=${encodeURIComponent(trackingInput.trim())}`);
    }
  };

  return (
    <section className="relative min-h-screen flex items-center overflow-hidden">
      <div className="absolute inset-0 bg-primary-navy"></div>

      <div
        className="absolute inset-0 bg-cover bg-center opacity-25"
        style={{
          backgroundImage: 'url(https://public.readdy.ai/ai/img_res/a31d687848bc7a35659ecb340834d2ca.jpg)',
        }}
      ></div>

      <div className="absolute inset-0 bg-primary-navy/60"></div>

      <div className="relative z-10 w-full max-w-7xl mx-auto px-4 md:px-6 lg:px-8 py-28 md:py-32">
        <div className="max-w-3xl">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl xl:text-7xl text-white font-bold leading-tight mb-4 md:mb-6">
              Logistique Mondiale
              <br />
              <span className="text-primary-green font-medium">Simplifiée.</span>
              <br />
              <span className="text-3xl md:text-4xl lg:text-5xl">Suivi en temps réel.</span>
            </h1>
          </motion.div>

          <motion.p
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="text-base md:text-lg lg:text-xl text-gray-300 mb-6 md:mb-8 max-w-2xl"
          >
            Suivi en temps réel dans plus de 50 pays. Vos envois, notre priorité.
          </motion.p>

          {/* ── Bloc tracking rapide ── */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.35 }}
            className="mb-6 md:mb-8"
          >
            <form onSubmit={handleTrack} className="flex flex-col sm:flex-row gap-2 w-full max-w-xl">
              <div className="flex-1 relative">
                <i className="ri-search-line absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 text-lg"></i>
                <input
                  type="text"
                  value={trackingInput}
                  onChange={(e) => setTrackingInput(e.target.value)}
                  placeholder="Ex : TV1234567890"
                  className="w-full h-13 pl-11 pr-4 py-3.5 text-sm bg-white/10 backdrop-blur-sm border border-white/30 text-white placeholder-gray-400 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-green focus:border-transparent transition-all"
                />
              </div>
              <button
                type="submit"
                className="h-13 px-6 py-3.5 bg-primary-green text-white font-semibold rounded-xl hover:bg-primary-green/90 transition-all flex items-center justify-center whitespace-nowrap cursor-pointer gap-2 text-sm"
              >
                <i className="ri-radar-line"></i>
                Suivre mon colis
              </button>
            </form>
            <p className="text-xs text-gray-400 mt-2 ml-1">Entrez votre numéro de suivi pour un résultat instantané</p>
          </motion.div>

          {/* ── Boutons CTA ── */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.5 }}
            className="flex flex-col sm:flex-row gap-3"
          >
            <Link
              to="/tracking"
              className="inline-flex items-center justify-center px-6 py-3.5 bg-white/10 border border-white/30 text-white font-semibold rounded-xl hover:bg-white/20 transition-all text-sm whitespace-nowrap cursor-pointer backdrop-blur-sm"
            >
              <i className="ri-map-pin-line mr-2"></i>
              Page de suivi complète
            </Link>
            <Link
              to="/services"
              className="inline-flex items-center justify-center px-6 py-3.5 bg-transparent border-2 border-white/50 text-white font-semibold rounded-xl hover:bg-white hover:text-primary-navy transition-all text-sm whitespace-nowrap cursor-pointer"
            >
              Nos services
            </Link>
          </motion.div>
        </div>

        {/* ── Stats flottantes ── */}
        <motion.div
          initial={{ opacity: 0, x: 50 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.8, delay: 0.6 }}
          className="absolute bottom-24 right-6 hidden lg:flex flex-col gap-3"
        >
          <div className="bg-white/10 backdrop-blur-md border border-white/20 rounded-xl p-5 w-36 text-center">
            <div className="text-3xl font-bold text-primary-green mb-1">{counts.countries}+</div>
            <div className="text-xs text-gray-300 font-medium">Pays couverts</div>
          </div>
          <div className="bg-white/10 backdrop-blur-md border border-white/20 rounded-xl p-5 w-36 text-center">
            <div className="text-3xl font-bold text-white mb-1">{counts.support}/7</div>
            <div className="text-xs text-gray-300 font-medium">Support</div>
          </div>
          <div className="bg-white/10 backdrop-blur-md border border-white/20 rounded-xl p-5 w-36 text-center">
            <div className="text-3xl font-bold text-primary-green mb-1">{counts.accuracy}%</div>
            <div className="text-xs text-gray-300 font-medium">Précision</div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default HeroSection;
